import {Injectable} from '@angular/core';
import {FirebaseApp} from 'firebase/app';
import {Auth, getAuth} from 'firebase/auth';
import {Database, getDatabase} from 'firebase/database';
import {Firestore, getFirestore} from 'firebase/firestore';
import {getStorage} from 'firebase/storage';
import {Analytics, getAnalytics} from 'firebase/analytics';
import {FirebasePerformance, getPerformance} from 'firebase/performance';

type FirebaseStorage = ReturnType<typeof getStorage>;

/**
 * Service centralisé pour l'accès aux services Firebase.
 *
 * Ce service fournit un accès unifié aux instances Firebase
 * initialisées dans l'application.
 */
@Injectable({
	providedIn: 'root'
})
export class FirebaseInitService {
	private _app: FirebaseApp | null = null;
	private _auth: Auth | null = null;
	private _firestore: Firestore | null = null;
	private _database: Database | null = null;
	private _storage: FirebaseStorage | null = null;
	private _analytics: Analytics | null = null;
	private _performance: FirebasePerformance | null = null;

	/**
	 * Initialise Firebase avec la configuration fournie.
	 *
	 * @param app - L'instance FirebaseApp initialisée
	 */
	initialize(app: FirebaseApp): void {
		this._app = app;
		this._auth = getAuth(app);
		this._firestore = getFirestore(app);
		this._database = getDatabase(app);
		this._storage = getStorage(app);
	}

	get app(): FirebaseApp {
		if (!this._app) {
			throw new Error('Firebase n\'est pas initialisé. Appelez initialize() d\'abord.');
		}
		return this._app;
	}

	get auth(): Auth {
		if (!this._auth) {
			throw new Error('Firebase Auth n\'est pas initialisé.');
		}
		return this._auth;
	}

	get firestore(): Firestore {
		if (!this._firestore) {
			throw new Error('Firestore n\'est pas initialisé.');
		}
		return this._firestore;
	}

	get database(): Database {
		if (!this._database) {
			throw new Error('Realtime Database n\'est pas initialisé.');
		}
		return this._database;
	}

	get storage(): FirebaseStorage {
		if (!this._storage) {
			throw new Error('Storage n\'est pas initialisé.');
		}
		return this._storage;
	}

	get analytics(): Analytics | null {
		if (!this._app || typeof window === 'undefined') {
			return null;
		}
		if (!this._analytics && this.hasAnalyticsConsent()) {
			this._analytics = getAnalytics(this._app);
		}
		return this._analytics;
	}

	get performance(): FirebasePerformance | null {
		if (!this._app || typeof window === 'undefined') {
			return null;
		}
		if (!this._performance && this.hasAnalyticsConsent()) {
			this._performance = getPerformance(this._app);
		}
		return this._performance;
	}

	private hasAnalyticsConsent(): boolean {
		return localStorage.getItem('cookiesConsent') === 'true';
	}
}

